import React from 'react';
import { fruits, weapons } from '../data/gameData';

const CharacterBuild = ({ stats, onStatChange, onFruitSelect, onWeaponSelect, darkMode }) => {
  const handleSliderChange = (stat, value) => {
    onStatChange(stat, value);
  };
  
  const handleFruitChange = (e) => {
    const selectedFruitId = e.target.value;
    const fruit = fruits.find(f => f.id === selectedFruitId) || null;
    onFruitSelect(fruit);
  };
  
  const handleWeaponChange = (e) => {
    const selectedWeaponId = e.target.value;
    const weapon = weapons.find(w => w.id === selectedWeaponId) || null;
    onWeaponSelect(weapon);
  };
  
  // Suggests ideal stat allocation based on selected fruit and weapon
  const getSuggestion = () => {
    return "Consider allocating more points to strength for a balanced build. For PvP, focus more on defense and stamina.";
  };
  
  const cardClass = darkMode 
    ? 'bg-card-dark shadow-card-dark border border-gray-700' 
    : 'bg-white shadow-card-light';
    
  const labelClass = darkMode 
    ? 'text-gray-300' 
    : 'text-gray-700';
    
  const inputBgClass = darkMode 
    ? 'bg-gray-700 border-gray-600' 
    : 'bg-gray-200';
    
  const inputClass = darkMode 
    ? 'bg-gray-800 border-gray-700 text-gray-200' 
    : 'border-gray-300 text-gray-900';
    
  return (
    <div className={`p-6 rounded-lg transition-theme duration-300 ease-in-out ${cardClass}`}>
      <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Character Build</h2>
      
      <div className="mb-6">
        <h3 className={`text-xl font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Stats</h3>
        
        <div className="space-y-5">
          <div>
            <div className="flex justify-between mb-1">
              <label className={`text-sm font-medium ${labelClass}`}>
                Strength:
              </label>
              <span className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{stats.strength}</span>
            </div>
            <input
              type="range"
              min="0"
              max="2500"
              value={stats.strength}
              onChange={(e) => handleSliderChange('strength', e.target.value)}
              className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${inputBgClass}`}
              style={{
                backgroundImage: darkMode 
                  ? `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${stats.strength/25}%, #4B5563 ${stats.strength/25}%, #4B5563 100%)`
                  : `linear-gradient(to right, #2563EB 0%, #2563EB ${stats.strength/25}%, #E5E7EB ${stats.strength/25}%, #E5E7EB 100%)`
              }}
            />
          </div>
          
          <div>
            <div className="flex justify-between mb-1">
              <label className={`text-sm font-medium ${labelClass}`}>
                Defense:
              </label>
              <span className={`text-sm font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{stats.defense}</span>
            </div>
            <input
              type="range"
              min="0"
              max="2500"
              value={stats.defense}
              onChange={(e) => handleSliderChange('defense', e.target.value)}
              className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${inputBgClass}`}
              style={{
                backgroundImage: darkMode 
                  ? `linear-gradient(to right, #10B981 0%, #10B981 ${stats.defense/25}%, #4B5563 ${stats.defense/25}%, #4B5563 100%)`
                  : `linear-gradient(to right, #059669 0%, #059669 ${stats.defense/25}%, #E5E7EB ${stats.defense/25}%, #E5E7EB 100%)`
              }}
            />
          </div>
          
          <div>
            <div className="flex justify-between mb-1">
              <label className={`text-sm font-medium ${labelClass}`}>
                Stamina:
              </label>
              <span className={`text-sm font-semibold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{stats.stamina}</span>
            </div>
            <input
              type="range"
              min="0"
              max="2500"
              value={stats.stamina}
              onChange={(e) => handleSliderChange('stamina', e.target.value)}
              className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${inputBgClass}`}
              style={{
                backgroundImage: darkMode 
                  ? `linear-gradient(to right, #F59E0B 0%, #F59E0B ${stats.stamina/25}%, #4B5563 ${stats.stamina/25}%, #4B5563 100%)`
                  : `linear-gradient(to right, #D97706 0%, #D97706 ${stats.stamina/25}%, #E5E7EB ${stats.stamina/25}%, #E5E7EB 100%)`
              }}
            />
          </div>
          
          <div>
            <div className="flex justify-between mb-1">
              <label className={`text-sm font-medium ${labelClass}`}>
                Fruit Mastery:
              </label>
              <span className={`text-sm font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{stats.fruitMastery}</span>
            </div>
            <input
              type="range"
              min="0"
              max="600"
              value={stats.fruitMastery}
              onChange={(e) => handleSliderChange('fruitMastery', e.target.value)}
              className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${inputBgClass}`}
              style={{
                backgroundImage: darkMode 
                  ? `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${stats.fruitMastery/6}%, #4B5563 ${stats.fruitMastery/6}%, #4B5563 100%)`
                  : `linear-gradient(to right, #7C3AED 0%, #7C3AED ${stats.fruitMastery/6}%, #E5E7EB ${stats.fruitMastery/6}%, #E5E7EB 100%)`
              }}
            />
          </div>
          
          <div>
            <div className="flex justify-between mb-1">
              <label className={`text-sm font-medium ${labelClass}`}>
                Sword Mastery:
              </label>
              <span className={`text-sm font-semibold ${darkMode ? 'text-pink-400' : 'text-pink-600'}`}>{stats.swordMastery}</span>
            </div>
            <input
              type="range"
              min="0"
              max="600"
              value={stats.swordMastery}
              onChange={(e) => handleSliderChange('swordMastery', e.target.value)}
              className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${inputBgClass}`}
              style={{
                backgroundImage: darkMode 
                  ? `linear-gradient(to right, #EC4899 0%, #EC4899 ${stats.swordMastery/6}%, #4B5563 ${stats.swordMastery/6}%, #4B5563 100%)`
                  : `linear-gradient(to right, #DB2777 0%, #DB2777 ${stats.swordMastery/6}%, #E5E7EB ${stats.swordMastery/6}%, #E5E7EB 100%)`
              }}
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              Level:
            </label>
            <input
              type="number"
              min="1"
              max="2500"
              value={stats.level}
              onChange={(e) => handleSliderChange('level', e.target.value)}
              className={`w-full p-2 border rounded transition-colors ${inputClass}`}
            />
          </div>
        </div>
      </div>
      
      <div className="mb-6">
        <h3 className={`text-xl font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Equipment</h3>
        
        <div className="space-y-5">
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              Fruit:
            </label>
            <select
              onChange={handleFruitChange}
              className={`w-full p-2 border rounded transition-colors ${inputClass}`}
            >
              <option value="">Select a Fruit</option>
              {fruits.map(fruit => (
                <option key={fruit.id} value={fruit.id}>{fruit.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              Weapon:
            </label>
            <select
              onChange={handleWeaponChange}
              className={`w-full p-2 border rounded transition-colors ${inputClass}`}
            >
              <option value="">Select a Weapon</option>
              {weapons.map(weapon => (
                <option key={weapon.id} value={weapon.id}>{weapon.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      <div className={`p-4 rounded-lg border ${
        darkMode 
          ? 'bg-blue-900/30 border-blue-800 text-blue-300' 
          : 'bg-blue-50 border-blue-200 text-blue-800'
      }`}>
        <h4 className={`text-lg font-semibold mb-2 ${
          darkMode ? 'text-blue-300' : 'text-blue-800'
        }`}>Suggestion</h4>
        <p>{getSuggestion()}</p>
      </div>
    </div>
  );
};

export default CharacterBuild;
